import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import QuantityStepper from '@ingka/quantity-stepper';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import Button from '@ingka/button';
import { ProductWithStock } from '@/lib/utils/database';
import { Item, OrderType } from '@/redux/back-dashboard-api';
import useYupValidationResolver from '@/lib/hooks/yup';
import { useContentTracking } from '@/lib/hooks/tracking';
import { combinationPickerSchema } from '@/lib/schemas/combination-picker';
import {
  useWebshopMode,
  WebshopModeProvider,
} from '@/components/page-widgets/webshop/context';
import { ProductCombinationPicker } from '@/components/containers/card-shop-product/combination-picker';

type ProductCombinationSiderProps = {
  product: ProductWithStock;
  preferredItem: Item;
  onSelect: (item: Item, amount: number) => void;
  onConfirmDismiss?: () => void;
};

/**
 * Internal sider content that uses context hooks.
 * Must be wrapped with WebshopModeProvider.
 */
const ProductCombinationSiderContent: FC<
  ModalComponentBaseProps & ProductCombinationSiderProps
> = ({
  handleDismiss,
  onSelect,
  onConfirmDismiss,
  product,
  preferredItem,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  const { showStock, allowOutOfStockOrders, getMaxQuantity } = useWebshopMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form
  const resolver = useYupValidationResolver(combinationPickerSchema);
  const form = useForm({
    mode: 'all',
    resolver,
    defaultValues: {
      amount: 1,
      color: preferredItem.combinations.color ?? undefined,
      fit: preferredItem.combinations.fit ?? undefined,
      size: preferredItem.combinations.size ?? undefined,
    },
    criteriaMode: 'all',
  });
  const selectedColor = form.watch('color');
  const selectedFit = form.watch('fit');
  const selectedSize = form.watch('size');
  const amount = form.watch('amount');
  // Data
  const selectedItem = useMemo(() => {
    const hasColorCombinations = product.combinations.color.size > 0;
    const hasFitCombinations = product.combinations.fit.size > 0;
    const hasSizeCombinations = product.combinations.size.size > 0;

    const candidates = product.items
      .filter((item) =>
        hasColorCombinations ? item.combinations.color === selectedColor : true
      )
      .filter((item) =>
        hasFitCombinations ? item.combinations.fit === selectedFit : true
      )
      .filter((item) =>
        hasSizeCombinations ? item.combinations.size === selectedSize : true
      );

    return candidates.length === 1 ? candidates[0] : null;
  }, [product, selectedColor, selectedFit, selectedSize]);

  const totalStock = selectedItem?.stock.available || 0;
  const minVal = 1;
  const maxVal = getMaxQuantity(totalStock);
  const isDisabled = !selectedItem || !amount || isSubmitting;
  // Event handlers
  const dismiss = async () => {
    if (isDisabled) return;
    setIsSubmitting(true);
    try {
      if (selectedItem) await onSelect(selectedItem, amount);
      handleDismiss();
      onConfirmDismiss?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset the selected amount of items to keep the value below the max stock
  useEffect(() => {
    if (maxVal < amount) {
      form.setValue('amount', maxVal);
    }
  }, [amount, totalStock, form, showStock, maxVal]);

  useEffect(() => {
    trackContent('product-combination', {
      type: 'sider',
      product: product.itemRef.type,
      productCategory: product.itemRef.category,
    });
  }, []);

  return (
    <Sheets
      {...rest}
      size="medium"
      data-testid="productCombinationSider"
      aria-label={product.itemRef.productName}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={product.itemRef.productName}
        />
      }
      footer={
        <ModalFooter className="gap-x-4 gap-y-2">
          <QuantityStepper
            disabled={!selectedItem}
            ariaDescribedBy={t('aria.quantityStepper')}
            ariaDescribedById="helper"
            ariaLabelDecrease={t('aria.decrease')}
            ariaLabelIncrease={t('aria.increase')}
            ariaLabelInput={t('quantitySet')}
            maxVal={maxVal}
            minVal={1}
            value={amount}
            onChange={(event) => {
              let value = parseInt(event.target.value, 10) || minVal;

              if (value > maxVal) value = maxVal;
              if (value < minVal) value = minVal;

              form.setValue('amount', value);
            }}
            onIncrease={() => form.setValue('amount', amount + 1)}
            onDecrease={() => form.setValue('amount', amount - 1)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />

          <Button type="primary" disabled={isDisabled} onClick={dismiss}>
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <ProductCombinationPicker
            product={product}
            showStock={showStock}
            allowOutOfStockOrders={allowOutOfStockOrders}
          />
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

/**
 * Product combination sider wrapper that provides the WebshopModeContext.
 * Receives orderType as prop and wraps content with the provider.
 */
const ProductCombinationSider: FC<
  ModalComponentBaseProps &
    ProductCombinationSiderProps & { orderType: OrderType }
> = ({ orderType, ...props }) => (
  <WebshopModeProvider orderType={orderType}>
    <ProductCombinationSiderContent {...props} />
  </WebshopModeProvider>
);

export default ProductCombinationSider;
